import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Space } from 'antd';
import { TeamOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { temModoPrestador } from '../../utils/moduloAccess';
import {
  ativarModoPrestador,
  PRESTADOR_BANNER_MIN_COBRANCAS,
} from '../../utils/modoPrestador';
import {
  dismissPrestadorBanner,
  isPrestadorBannerDismissed,
  isPrestadorBannerEligible,
  markPrestadorBannerEligible,
} from '../../utils/prestadorBannerStorage';
import { getAReceber } from '../../services/clienteService';
import { updatePerfil } from '../../services/userService';

/**
 * Banner após volume de cobranças (≥3) oferecendo ativar o Modo Prestador.
 */
function ModoPrestadorBanner() {
  const navigate = useNavigate();
  const { user, refreshUser, temModuloFinanceiro } = useAuth();
  const userId = user?.id ?? user?.pk;
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function check() {
      if (!temModuloFinanceiro || !user || temModoPrestador(user)) {
        setVisible(false);
        return;
      }
      if (isPrestadorBannerDismissed(userId)) {
        setVisible(false);
        return;
      }

      let eligible = isPrestadorBannerEligible(userId);
      if (!eligible) {
        try {
          const data = await getAReceber();
          const qtd = Number(data?.qtd_cobrancas) || 0;
          if (qtd >= PRESTADOR_BANNER_MIN_COBRANCAS) {
            markPrestadorBannerEligible(userId);
            eligible = true;
          }
        } catch {
          /* ignore */
        }
      }
      if (!cancelled) setVisible(Boolean(eligible));
    }

    check();
    return () => {
      cancelled = true;
    };
  }, [temModuloFinanceiro, user, userId]);

  const handleDismiss = useCallback(() => {
    dismissPrestadorBanner(userId);
    setVisible(false);
  }, [userId]);

  const handleAtivar = useCallback(async () => {
    setLoading(true);
    try {
      await ativarModoPrestador(updatePerfil);
      await refreshUser();
      dismissPrestadorBanner(userId);
      setVisible(false);
      navigate('/a-receber');
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [navigate, refreshUser, userId]);

  if (!visible) return null;

  return (
    <Alert
      type="info"
      showIcon
      icon={<TeamOutlined />}
      style={{ marginBottom: 24 }}
      message="Ferramentas para quem cobra com frequência"
      description="Você já fez várias cobranças. Ative Clientes e o painel A receber — cobrança ocasional de amigos continua funcionando sem isso."
      action={
        <Space direction="vertical" size={4}>
          <Button type="primary" size="small" loading={loading} onClick={handleAtivar}>
            Ativar agora
          </Button>
          <Button type="link" size="small" onClick={handleDismiss}>
            Agora não
          </Button>
        </Space>
      }
      closable
      onClose={handleDismiss}
    />
  );
}

export default ModoPrestadorBanner;
